// TICKET-SALES-017 — when an existing lead matches the customer's phone, say so
// and offer to link it in one click.
//
// TICKET-SALES-021 moved that question to STEP 1, where the rep is typing the
// client, and demoted the dialog to a fallback. At save time "this matches an
// existing lead" reads as "you just duplicated work" and the honest response is
// to click past it; at step 1 the same sentence is a shortcut. One decision per
// booking, not two.
//
// The rule that shapes everything here, unchanged since SALES-017: this must
// NEVER be the reason a booking does not get made. A false positive with a
// customer at the desk is worse than a missed link. So a failed or slow lookup
// saves straight through and both buttons in the fallback dialog save. The X and
// Escape are inert for that reason, not to trap anyone.
//
// What the notice does NOT do any more: be ignorable. Silence read as an answer
// is not an answer — it is the rep not having seen the box. So a match that is
// on screen and unanswered holds Continue until one of the two buttons is
// pressed, and BOTH are real buttons. The gate exists only while there is a
// question on screen: a lookup that is slow, failed or found nothing never
// disables anything, and a new phone number re-arms the question rather than
// inheriting the last one's answer.
import { useCallback, useRef, useState } from "react";
import { api } from "../lib/api";
import { Button } from "./ui/button";
import { StatusPill } from "./lux";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { UserCircle, LinkSimple, Info, LinkBreak } from "@phosphor-icons/react";

/** Stated beside Continue, so the disabled button is never a mystery. */
export const LEAD_MATCH_BLOCK_REASON = "This number matches a lead — answer it above to continue";

const STATUS_LABEL = {
  new: "New", contacted: "Contacted", quoted: "Quoted", won: "Won", lost: "Lost",
};

const fmtDay = (s) => {
  if (!s) return "date unknown";
  const d = new Date(s);
  return Number.isNaN(d.getTime())
    ? "date unknown"
    : d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
};

// The server's `_phone_key`, digit for digit: the last ten digits, or nothing
// under ten. Mirrored so the client knows when the phone it is holding is still
// the phone a decision was made about — never to decide a match itself.
export const phoneKey = (value) => {
  const digits = String(value || "").replace(/\D/g, "");
  return digits.length >= 10 ? digits.slice(-10) : "";
};

/**
 * Wraps a booking save with the lead lookup, and owns the step-1 notice.
 *
 * `save(payload, opts)` is the page's own POST. `onLinkChange(leadId)` is how
 * the page puts `lead_id` on (or takes it off) its form — the hook never
 * touches the form itself.
 */
export function useLeadMatch(save, { onLinkChange } = {}) {
  const [pending, setPending] = useState(null);     // the fallback dialog
  const [notice, setNotice] = useState(null);       // { key, candidates } shown at step 1
  const [linked, setLinked] = useState(null);       // { key, leadId, name, candidates }
  const [dropped, setDropped] = useState(null);     // { name, reason: "phone" | "stale" }
  const [answer, setAnswer] = useState(null);       // { key, kind: "linked" | "not_same" }
  const timer = useRef(null);
  // key -> candidates[]. Also the record of WHICH phones were looked up, which
  // is what decides whether the save-time dialog is still owed. A failed lookup
  // is deliberately not cached: it did not happen.
  const looked = useRef(new Map());
  const linkedRef = useRef(null);
  // The answer is read by `start`, which runs from an event handler and would
  // otherwise close over whatever the last render happened to see.
  const answerRef = useRef(null);
  const lastKey = useRef(null);
  const setLink = (v) => { linkedRef.current = v; setLinked(v); };
  const setAnswered = (v) => { answerRef.current = v; setAnswer(v); };

  const lookup = useCallback(async (key, phone) => {
    if (looked.current.has(key)) return looked.current.get(key);
    try {
      const { data } = await api.get("/leads/match", { params: { phone } });
      const candidates = data?.candidates || [];
      looked.current.set(key, candidates);
      return candidates;
    } catch {
      return null;   // null = the question was never answered, [] = answered "none"
    }
  }, []);

  /** Every phone keystroke and every pick from the customer picker. Debounced. */
  const check = useCallback((phone) => {
    const key = phoneKey(phone);
    clearTimeout(timer.current);
    const was = linkedRef.current;
    if (was && was.key !== key) {
      // SALES-021: the phone no longer matches the lead this booking was linked
      // to, so the link goes. A link to the wrong customer is worse than none.
      // Anything already filled in stays — the rep may have edited it.
      onLinkChange?.("");
      setLink(null);
      setDropped({ name: was.name, reason: "phone" });
    }
    // A different number re-arms the question: the previous answer was about a
    // different person, and a notice left over from the old number must not gate
    // Continue on one nobody has been asked about yet.
    if (lastKey.current !== key) {
      lastKey.current = key;
      if (answerRef.current) setAnswered(null);
      setNotice(null);
    }
    if (!key) return;
    timer.current = setTimeout(async () => {
      const candidates = await lookup(key, phone);
      setNotice(candidates && candidates.length >= 2 ? { key, candidates } : null);
    }, 450);
  }, [lookup, onLinkChange]);

  const use = (candidate) => {
    const key = notice?.key || "";
    setLink({ key, leadId: candidate.id, name: candidate.customer_name, candidates: notice?.candidates.length || 1 });
    setAnswered({ key, kind: "linked" });
    setDropped(null);
    onLinkChange?.(candidate.id);
  };

  /** The other half of the question — a pressed button, not an unread notice. */
  const notSame = () => {
    if (!notice) return;
    setAnswered({ key: notice.key, kind: "not_same" });
  };

  /**
   * A restored draft carries a `lead_id` from a previous session. Saving a
   * booking against a lead that has since been converted or marked lost would
   * attach it to the wrong thing, so the link is checked against the same
   * lookup — which excludes exactly those two states.
   *
   * Returns true when the link stands. A lookup that FAILS leaves the link
   * alone: a network error is not evidence of staleness.
   */
  const revalidate = async (leadId, phone) => {
    const key = phoneKey(phone);
    if (!leadId || !key) return true;
    const candidates = await lookup(key, phone);
    if (candidates === null) return true;
    const hit = candidates.find((c) => c.id === leadId);
    if (hit) {
      setLink({ key, leadId, name: hit.customer_name, candidates: candidates.length });
      setNotice({ key, candidates });
      // The draft's own link IS the answer for this number: a restored booking
      // that is already linked must not be held at step 1.
      setAnswered({ key, kind: "linked" });
      lastKey.current = key;
      return true;
    }
    onLinkChange?.("");
    setLink(null);
    setNotice(candidates.length >= 2 ? { key, candidates } : null);
    setDropped({ name: "", reason: "stale" });
    return false;
  };

  /** A new booking starts with no memory of the last one's decision. */
  const reset = () => {
    clearTimeout(timer.current);
    looked.current.clear();
    lastKey.current = null;
    setLink(null);
    setAnswered(null);
    setNotice(null);
    setDropped(null);
    setPending(null);
  };

  const start = async (payload) => {
    const key = phoneKey(payload.customer_phone);
    const decided = linkedRef.current;
    const answered = answerRef.current;
    // Decided at step 1, and the phone still says so: save it, with the number
    // of candidates the rep was actually shown.
    if (key && decided && decided.key === key && decided.leadId) {
      return save(
        { ...payload, lead_id: decided.leadId, lead_match: { decision: "linked", candidates: decided.candidates } },
        { linked: true },
      );
    }
    // "Not the same person" was pressed for THIS phone. That is an answer, so
    // it is recorded and there is no second prompt.
    if (key && answered && answered.key === key && answered.kind === "not_same") {
      const shown = (looked.current.get(key) || []).length;
      return save(shown ? { ...payload, lead_match: { decision: "dismissed", candidates: shown } } : payload);
    }
    if (key && looked.current.has(key)) {
      const shown = looked.current.get(key) || [];
      // SALES-033: 0 or 1 match sends NO lead choice — with exactly one the
      // server links it automatically (no dialog) and the page confirms it from
      // `auto_linked`; with none the server creates the booking's own lead.
      if (shown.length < 2) return save(payload);
      // Two or more, and no answer. Continue is held while the notice is up, so
      // the only way here is the lookup landing after the rep left step 1 —
      // which is what the fallback is for. Silence is not taken as consent.
      return setPending({ payload, candidates: shown });
    }
    // Fallback (SALES-017): no step-1 lookup ever completed for this number —
    // the rep reached the end before the debounce fired, or it failed.
    if (payload.lead_id || !key) return save(payload);
    const candidates = await lookup(key, String(payload.customer_phone || "").trim());
    // SALES-033: 0 or 1 match → no choice sent; the server decides (auto-link on
    // a single match). Only two or more opens the dialog.
    if (!candidates || candidates.length < 2) return save(payload);
    setPending({ payload, candidates });
  };

  const decide = (leadId) => {
    if (!pending) return undefined;
    const { payload, candidates } = pending;
    setPending(null);
    return save(
      {
        ...payload,
        ...(leadId ? { lead_id: leadId } : {}),
        lead_match: { decision: leadId ? "linked" : "dismissed", candidates: candidates.length },
      },
      { linked: !!leadId },
    );
  };

  // The gate: a question on screen that has not been answered for the number
  // currently in the field. A slow, failed or empty lookup never sets `notice`,
  // so none of those can disable anything.
  const unanswered = Boolean(notice?.candidates?.length) && answer?.key !== notice.key;

  return {
    start,
    check,
    revalidate,
    reset,
    // Everything StepClient draws under the phone field.
    inline: {
      candidates: linked || answer ? [] : (notice?.candidates || []),
      linked,
      dropped,
      notSame: answer?.kind === "not_same" ? (notice?.candidates?.length || 1) : 0,
      onUse: use,
      onNotSame: notSame,
      blocked: unanswered,
      reason: unanswered ? LEAD_MATCH_BLOCK_REASON : "",
    },
    dialog: {
      open: !!pending,
      candidates: pending?.candidates || [],
      onLink: (leadId) => decide(leadId),
      onDismiss: () => decide(null),
    },
  };
}

/**
 * SALES-021 — the step-1 notice. Inline, never a modal, and answered in place:
 * two real buttons, because a notice nobody clicks is a notice nobody read.
 * Rounded to the same `rounded-xl` as the phone input it sits under.
 */
export function LeadMatchNotice({ candidates, linked, dropped, notSame, onUse, onNotSame }) {
  if (!candidates.length && !linked && !dropped && !notSame) return null;
  const many = candidates.length > 1;
  return (
    <div className="mt-2.5 space-y-2" data-testid="lead-match-notice">
      {dropped && (
        <div className="flex items-start gap-2 rounded-xl border border-amber-500/30 bg-amber-500/[0.06] px-3 py-2 text-xs text-amber-200/90"
             data-testid="lead-match-dropped">
          <LinkBreak size={14} className="mt-0.5 shrink-0" />
          <span>
            {dropped.reason === "stale"
              ? "The lead this draft was linked to is no longer open — the link was removed."
              : `Link removed${dropped.name ? ` from ${dropped.name}` : ""} — this phone no longer matches that lead. Anything already filled in was left as it is.`}
          </span>
        </div>
      )}

      {linked && (
        <div className="flex items-start gap-2 rounded-xl border border-emerald-500/30 bg-emerald-500/[0.06] px-3 py-2 text-xs"
             data-testid="lead-match-linked">
          <LinkSimple size={14} className="mt-0.5 shrink-0 text-emerald-400" />
          <span>
            Linked to <b className="text-foreground" data-testid="lead-match-linked-name">{linked.name || "this lead"}</b>
            {"'s lead — it is marked won when the booking is submitted."}
          </span>
        </div>
      )}

      {/* Answered "no". Said back plainly, so the rep can see it registered. */}
      {notSame > 0 && (
        <div className="flex items-start gap-2 rounded-xl border border-border bg-secondary/25 px-3 py-2 text-xs text-muted-foreground"
             data-testid="lead-match-not-same">
          <LinkBreak size={14} className="mt-0.5 shrink-0" />
          <span>Not the same person — nothing was linked. A lead will be created for this booking.</span>
        </div>
      )}

      {candidates.map((c, i) => (
        <div
          key={c.id}
          className="rounded-xl border border-primary/30 bg-primary/[0.05] px-3 py-2.5"
          data-testid={`lead-match-notice-candidate-${i}`}
        >
          <div className="flex items-start gap-2">
            <Info size={14} className="mt-0.5 shrink-0 text-primary" />
            <div className="min-w-0 flex-1">
              <div className="text-xs flex items-center gap-x-2 gap-y-1 flex-wrap">
                <b className="text-foreground" data-testid={`lead-match-notice-candidate-${i}-name`}>
                  {c.customer_name || "Unnamed lead"}
                </b>
                <StatusPill status={c.status} testid={`lead-match-notice-candidate-${i}-status`}>
                  {STATUS_LABEL[c.status] || c.status}
                </StatusPill>
                <span className="text-muted-foreground">is already a lead</span>
              </div>
              {/* The owner is always drawn, as in SALES-017: a rep has to see the
                  lead is not theirs BEFORE they link it. */}
              <div className="text-[11px] text-muted-foreground mt-1 flex items-center gap-1.5 flex-wrap"
                   data-testid={`lead-match-notice-candidate-${i}-owner`}>
                <UserCircle size={12} />
                {c.salesperson ? `${c.salesperson}'s lead` : "Nobody owns this lead"}
                <span className="text-muted-foreground/60">·</span>
                <span data-testid={`lead-match-notice-candidate-${i}-received`}>came in {fmtDay(c.received_at)}</span>
              </div>
            </div>
          </div>
          <div className="mt-2.5 flex items-center gap-2 flex-wrap">
            <Button
              size="sm"
              onClick={() => onUse(c)}
              className="flex-1 min-w-[140px] rounded-full bg-primary text-primary-foreground hover:bg-primary/90"
              data-testid={`lead-match-use-${i}`}
            >
              <LinkSimple size={13} className="mr-1.5" /> Use this lead
            </Button>
            {/* One "no" for the whole list — it answers the question, not a row. */}
            {!many && (
              <Button
                size="sm"
                variant="outline"
                onClick={onNotSame}
                className="flex-1 min-w-[150px] rounded-full"
                data-testid="lead-match-not-same-button"
              >
                Not the same person
              </Button>
            )}
          </div>
        </div>
      ))}

      {many && (
        <Button
          size="sm"
          variant="outline"
          onClick={onNotSame}
          className="w-full rounded-full"
          data-testid="lead-match-not-same-button"
        >
          None of these — not the same person
        </Button>
      )}
    </div>
  );
}

export function LeadMatchDialog({ open, candidates, onLink, onDismiss }) {
  const many = candidates.length > 1;
  return (
    <Dialog open={open}>
      <DialogContent
        className="bg-card border-border max-w-xl [&>button]:hidden"
        onEscapeKeyDown={(e) => e.preventDefault()}
        onInteractOutside={(e) => e.preventDefault()}
        data-testid="lead-match-dialog"
      >
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl">
            {many ? `${candidates.length} leads have this phone number` : "This customer is already a lead"}
          </DialogTitle>
          <DialogDescription>
            Link the booking to the lead and it is marked won, so the sale counts against the
            work that brought it in. If it is not the same person, create the booking anyway —
            nothing is lost either way.
          </DialogDescription>
        </DialogHeader>

        <ul className="space-y-2.5" data-testid="lead-match-candidates">
          {candidates.map((c, i) => (
            <li
              key={c.id}
              className="border border-border/60 bg-secondary/20 p-4 flex items-center justify-between gap-4 flex-wrap"
              data-testid={`lead-match-candidate-${i}`}
            >
              <div className="min-w-0">
                <div className="flex items-baseline gap-x-2.5 gap-y-1 flex-wrap">
                  <span className="font-medium" data-testid={`lead-match-candidate-${i}-name`}>
                    {c.customer_name || "Unnamed lead"}
                  </span>
                  <span className="label-mono text-primary border border-primary/40 px-2 py-0.5 text-[10px]"
                        data-testid={`lead-match-candidate-${i}-status`}>
                    {STATUS_LABEL[c.status] || c.status}
                  </span>
                </div>
                {/* The owner is a plain line of text, always drawn: a rep has to
                    see the lead is not theirs BEFORE they link it, without
                    hovering or expanding anything. */}
                <div className="text-sm text-muted-foreground mt-1.5 flex items-center gap-1.5"
                     data-testid={`lead-match-candidate-${i}-owner`}>
                  <UserCircle size={14} />
                  {c.salesperson ? `${c.salesperson}'s lead` : "Nobody owns this lead"}
                  <span className="text-muted-foreground/60">·</span>
                  <span data-testid={`lead-match-candidate-${i}-received`}>came in {fmtDay(c.received_at)}</span>
                </div>
              </div>
              <Button
                size="sm"
                onClick={() => onLink(c.id)}
                className="bg-primary text-primary-foreground hover:bg-primary/90 shrink-0"
                data-testid={`lead-match-link-${i}`}
              >
                <LinkSimple size={14} className="mr-1.5" />
                Link to this lead
              </Button>
            </li>
          ))}
        </ul>

        <div className="mt-1">
          <Button variant="outline" onClick={onDismiss} className="w-full" data-testid="lead-match-dismiss">
            Not the same person — create anyway
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
